import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  MessageCircle, 
  MapPin, 
  DollarSign,
  Sparkles,
  ArrowRight,
  Loader2,
  Image as ImageIcon,
  Check,
  Clock,
  Info,
  Star,
  Crown,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  AlertTriangle,
  Tag,
  Ruler,
  Package,
  CheckCircle2,
  Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import { TrustBadge } from '../components/TrustScoreDisplay';
import { BuyerReviewForm, OwnerReviewForm } from '../components/ReviewComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MatchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [sendingInterest, setSendingInterest] = useState(false);
  const [specialInterestRemaining, setSpecialInterestRemaining] = useState(null);
  const [showJustification, setShowJustification] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewStatus, setReviewStatus] = useState(null);
  
  useEffect(() => {
    fetchMatch();
    fetchSpecialInterestStatus();
  }, [id]);

  useEffect(() => {
    if (match?.status === 'completed') {
      fetchReviewStatus();
    }
  }, [match?.status]);

  const fetchMatch = async () => {
    try {
      const response = await api.get(`/matches/${id}`);
      setMatch(response.data);
    } catch (error) {
      console.error('Failed to fetch match:', error);
      toast.error('Failed to load match details');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewStatus = async () => {
    try {
      const response = await api.get(`/matches/${id}/review-status`);
      setReviewStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch review status:', error);
    }
  };

  const fetchSpecialInterestStatus = async () => {
    try {
      const response = await api.get('/matches/special-interest/remaining');
      setSpecialInterestRemaining(response.data);
    } catch (error) {
      console.error('Failed to fetch special interest status:', error);
    }
  };

  const handleCompleteTransaction = async () => {
    setCompleting(true);
    try {
      const response = await api.post(`/matches/${id}/complete`);
      toast.success(response.data.message || 'Deal marked as complete!');
      fetchMatch();
      fetchReviewStatus();
      setShowReviewForm(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete deal');
    } finally {
      setCompleting(false);
    }
  };

  const handleSpecialInterest = async () => {
    setSendingInterest(true);
    try {
      const response = await api.post(`/matches/${id}/special-interest`);
      toast.success('Special Interest sent! The owner will be notified.');
      fetchMatch();
      fetchSpecialInterestStatus();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Upgrade to Premium for Special Interest prompts!');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send Special Interest');
      }
    } finally {
      setSendingInterest(false);
    }
  };

  const handleDownloadDocument = async (type) => {
    try {
      const txId = match.transaction_id;
      if (!txId) {
        toast.error('No transaction linked to this match');
        return;
      }
      const response = await api.get(`/transactions/${txId}/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `WANTD-${type === 'receipt' ? 'Receipt' : 'Invoice'}-${txId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Failed to download ${type}`);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return null;
    // Handle new image object format {ref, url, expires_at}
    if (typeof img === 'object' && img.url) {
      return img.url;
    }
    // Handle string URLs
    if (typeof img === 'string') {
      if (img.startsWith('http')) return img;
      return `${API_URL}${img}`;
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-32 bg-[#E5DDD3] rounded" />
            <div className="h-64 bg-[#E5DDD3] rounded-2xl" />
            <div className="h-48 bg-[#E5DDD3] rounded-2xl" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!match) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 text-center py-16">
          <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">Match Not Found</h2>
          <Button onClick={() => navigate('/matches')} className="rounded-full bg-[#1E3A5F] hover:bg-[#2D4A6F]">
            Go to Matches
          </Button>
        </div>
      </Layout>
    );
  }

  const isWanter = match.wanter_id === user?.id;
  const isOwner = match.owner_id === user?.id;
  
  // Get first images
  const wanterImage = match.wtb_images?.[0];
  const ownerImage = match.inventory_images?.[0];
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-[#059669]';
    if (score >= 50) return 'text-orange-500';
    return 'text-red-500';
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 md:px-6">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/matches')} 
          className="flex items-center gap-2 text-[#6B7280] hover:text-[#1E3A5F] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to matches
        </button>

        {/* Special Interest Badge (if shown) */}
        {match.has_special_interest && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-[#C9956C] to-[#D4A574] text-white mb-6">
            <div className="flex items-center gap-3">
              <Star className="w-6 h-6 fill-white" />
              <div>
                <p className="font-bold">Special Interest Match!</p>
                <p className="text-sm text-white/80">
                  {isOwner ? `@${match.wanter_username} is highly motivated to buy!` : "You've shown special interest"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Match Score Header */}
        <div className="p-6 rounded-2xl bg-white border border-[#E5DDD3] shadow-card mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Sparkles className={`w-6 h-6 ${getScoreColor(match.match_score)}`} />
              <span className={`text-4xl font-black ${getScoreColor(match.match_score)}`}>
                {Math.round(match.match_score)}%
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#059669]/10 border border-[#059669]/20">
              <Check className="w-5 h-5 text-[#059669]" />
              <span className="font-medium text-[#059669]">Active</span>
            </div>
          </div>
          
          {/* Justification Summary */}
          {match.justification && (
            <div className="mb-4">
              <p className="text-sm font-medium text-[#1E3A5F] mb-2">
                {match.justification.recommendation}
              </p>
              
              {/* Commentary - Conversational explanation */}
              {match.justification.commentary && (
                <p className="text-sm text-[#6B7280] mb-3 leading-relaxed" data-testid="match-commentary">
                  {match.justification.commentary}
                </p>
              )}
              
              {match.justification.strengths?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {match.justification.strengths.slice(0, 2).map((strength, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#059669]/10 text-[#059669] text-xs">
                      <Check className="w-3 h-3" />
                      {strength}
                    </span>
                  ))}
                </div>
              )}
              {match.justification.concerns?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {match.justification.concerns.slice(0, 2).map((concern, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F59E0B]/10 text-[#B45309] text-xs">
                      <AlertTriangle className="w-3 h-3" />
                      {concern}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Expand/Collapse Score Breakdown */}
          {match.score_breakdown && (
            <button
              onClick={() => setShowJustification(!showJustification)}
              className="w-full flex items-center justify-between py-2 text-sm text-[#1E3A5F] hover:text-[#C9956C] transition-colors"
              data-testid="toggle-justification"
            >
              <span className="font-medium">View Score Breakdown</span>
              {showJustification ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
          
          {/* Detailed Score Breakdown */}
          {showJustification && match.score_breakdown && (
            <div className="mt-4 pt-4 border-t border-[#E5DDD3] space-y-3" data-testid="score-breakdown">
              {Object.entries(match.score_breakdown).map(([factor, data]) => {
                const icons = {
                  category: Package,
                  price: DollarSign,
                  distance: MapPin,
                  condition: Star,
                  tags: Tag,
                  semantic: Sparkles
                };
                const Icon = icons[factor] || Info;
                const scoreColor = data.score >= 70 ? 'text-[#059669]' : data.score >= 50 ? 'text-[#F59E0B]' : 'text-[#DC2626]';
                
                return (
                  <div key={factor} className="flex items-center gap-3">
                    <Icon className={`w-4 h-4 ${scoreColor} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-[#1E3A5F] capitalize">{factor}</span>
                        <span className={`text-xs font-bold ${scoreColor}`}>{data.score}%</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-[#E5DDD3] overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${data.score >= 70 ? 'bg-[#059669]' : data.score >= 50 ? 'bg-[#F59E0B]' : 'bg-[#DC2626]'}`}
                          style={{ width: `${data.score}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-[#6B7280] mt-1 truncate">{data.reason}</p>
                    </div>
                    <span className="text-[10px] text-[#9CA3AF] flex-shrink-0">
                      {data.contribution !== undefined ? `+${data.contribution.toFixed(1)}` : data.weight !== undefined ? `${Math.round(data.weight * 100)}%` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          
          <p className="text-[#6B7280] mt-2">Match Score</p>
        </div>

        {/* Side-by-Side Match Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Left: What you're looking for / What wanter wants */}
          <div className="p-5 rounded-2xl bg-white border-2 border-[#C9956C]/40 shadow-card">
            <p className="text-sm font-bold uppercase tracking-wider text-[#C9956C] mb-4">
              {isWanter ? "What you're looking for" : `What @${match.wanter_username} wants`}
            </p>
            
            {/* Photo */}
            <div className="relative mb-4">
              <div className="aspect-square rounded-xl overflow-hidden bg-[#F5F0E8] border border-[#E5DDD3]">
                {wanterImage ? (
                  <img 
                    src={getImageUrl(wanterImage)} 
                    alt="What they want" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#C9956C]/50">
                    <ImageIcon className="w-12 h-12 mb-2" />
                    <span className="text-sm">No image</span>
                  </div>
                )}
              </div>
              <span className="absolute top-3 left-3 px-2.5 py-1 text-xs font-bold uppercase bg-[#C9956C] text-white rounded-full">
                Wanted
              </span>
            </div>
            
            {/* Details */}
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-[#1E3A5F] leading-tight">{match.wtb_title}</h3>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#059669]" />
                <span className="text-[#059669] font-semibold">
                  ${match.price_overlap?.wtb_min ?? '?'} - ${match.price_overlap?.wtb_max ?? '?'}
                </span>
                <span className="text-xs text-[#6B7280]">(budget)</span>
              </div>
              {!isWanter && (
                <p className="text-xs text-[#6B7280]">by @{match.wanter_username}</p>
              )}
            </div>
          </div>

          {/* Right: What you have / What owner has */}
          <div className="p-5 rounded-2xl bg-white border-2 border-[#1E3A5F]/40 shadow-card">
            <p className="text-sm font-bold uppercase tracking-wider text-[#1E3A5F] mb-4">
              {isOwner ? "What you have" : `What @${match.owner_username} has`}
            </p>
            
            {/* Photo */}
            <div className="relative mb-4">
              <div className="aspect-square rounded-xl overflow-hidden bg-[#F5F0E8] border border-[#E5DDD3]">
                {ownerImage ? (
                  <img 
                    src={getImageUrl(ownerImage)} 
                    alt="Available item" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#1E3A5F]/50">
                    <ImageIcon className="w-12 h-12 mb-2" />
                    <span className="text-sm">No image</span>
                  </div>
                )}
              </div>
              <span className="absolute top-3 left-3 px-2.5 py-1 text-xs font-bold uppercase bg-[#1E3A5F] text-white rounded-full">
                Available
              </span>
            </div>
            
            {/* Details */}
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-[#1E3A5F] leading-tight">{match.inventory_title}</h3>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#1E3A5F]" />
                <span className="text-[#1E3A5F] font-semibold">
                  ~${match.price_overlap?.inventory_value ?? '?'}
                </span>
                <span className="text-xs text-[#6B7280]">(valuation)</span>
              </div>
              {!isOwner && (
                <p className="text-xs text-[#6B7280]">from @{match.owner_username}</p>
              )}
            </div>
          </div>
        </div>

        {/* Distance */}
        <div className="p-4 rounded-xl bg-white border border-[#E5DDD3] shadow-card mb-6">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-[#C9956C]" />
            <div>
              <div className="text-[#1E3A5F] font-medium">{match.distance_km} km apart</div>
              <div className="text-sm text-[#6B7280]">Great for a local meetup!</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <Link to={`/messages/${match.id}`}>
            <Button className="w-full bg-[#059669] hover:bg-[#047857] rounded-full h-14 text-lg" data-testid="chat-btn">
              <MessageCircle className="w-5 h-5 mr-2" />
              Open Chat
            </Button>
          </Link>

          {/* Special Interest — wanters can still signal high motivation */}
          {isWanter && !match.has_special_interest && (
            <div className="p-5 rounded-xl bg-gradient-to-br from-[#C9956C]/10 to-[#1E3A5F]/5 border border-[#C9956C]/30 shadow-card">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-[#C9956C]" />
                <h3 className="font-bold text-[#1E3A5F]">Show Special Interest</h3>
                {specialInterestRemaining?.is_premium && (
                  <span className="px-2 py-0.5 rounded-full bg-[#C9956C] text-white text-xs font-bold flex items-center gap-1">
                    <Crown className="w-3 h-3" />
                    Premium
                  </span>
                )}
              </div>
              <p className="text-sm text-[#6B7280] mb-4">
                Let @{match.owner_username} know you&apos;re <strong>highly motivated</strong> — Special Interest alerts stand out in their notifications.
              </p>
              <div className="flex items-center justify-between mb-3 text-xs text-[#6B7280]">
                {specialInterestRemaining?.is_premium ? (
                  <span>{specialInterestRemaining.remaining} of 5 remaining this week</span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Crown className="w-3 h-3 text-[#C9956C]" />
                    Premium feature — 5/week
                  </span>
                )}
              </div>
              <Button
                onClick={handleSpecialInterest}
                className="w-full bg-[#C9956C] hover:bg-[#B8845B] rounded-full h-11"
                disabled={sendingInterest || (specialInterestRemaining && !specialInterestRemaining.is_premium && specialInterestRemaining.remaining <= 0)}
                data-testid="special-interest-btn"
              >
                {sendingInterest ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Star className="w-4 h-4 mr-2" />
                    Send Special Interest
                  </>
                )}
              </Button>
              {!specialInterestRemaining?.is_premium && (
                <p className="text-xs text-[#9CA3AF] text-center mt-2">
                  <Link to="/profile" className="text-[#C9956C] hover:underline">Upgrade to Premium</Link> for 5 Special Interests per week
                </p>
              )}
            </div>
          )}

          {isWanter && match.has_special_interest && (
            <div className="p-4 rounded-xl bg-[#C9956C]/10 border border-[#C9956C]/30">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#C9956C] fill-[#C9956C]" />
                <p className="text-sm text-[#1E3A5F] font-medium">
                  Special Interest sent! @{match.owner_username} has been notified.
                </p>
              </div>
            </div>
          )}

            {/* Completed Status Banner */}
            {match.status === 'completed' && (
              <div className="p-4 rounded-xl bg-[#059669]/10 border border-[#059669]/30">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle2 className="w-6 h-6 text-[#059669]" />
                  <div>
                    <p className="font-bold text-[#059669]">Deal Complete!</p>
                    <p className="text-sm text-[#6B7280]">Completed on {new Date(match.completed_at).toLocaleDateString()}</p>
                  </div>
                </div>
                {match.transaction_id && (
                  <div className="flex gap-2">
                    {isWanter && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadDocument('receipt')}
                        className="flex-1 rounded-full border-[#059669]/30 text-[#059669] hover:bg-[#059669]/10"
                      >
                        <Download className="w-4 h-4 mr-1.5" />
                        Receipt
                      </Button>
                    )}
                    {isOwner && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadDocument('invoice')}
                        className="flex-1 rounded-full border-[#059669]/30 text-[#059669] hover:bg-[#059669]/10"
                      >
                        <Download className="w-4 h-4 mr-1.5" />
                        Fee Invoice
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Review Section - Only show for completed transactions */}
            {match.status === 'completed' && reviewStatus && (
              <div className="p-5 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
                {!reviewStatus.has_reviewed && reviewStatus.can_review ? (
                  // Can leave a review
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <Star className="w-5 h-5 text-[#F59E0B]" />
                      <h3 className="font-bold text-[var(--theme-text-primary)]">Leave a Review</h3>
                    </div>
                    {!showReviewForm ? (
                      <>
                        <p className="text-sm text-[var(--theme-text-secondary)] mb-4">
                          Share your experience with @{reviewStatus.my_role === 'buyer' ? match.owner_username : match.wanter_username}. Your review helps build trust in the community!
                        </p>
                        <Button
                          onClick={() => setShowReviewForm(true)}
                          className="w-full bg-[#F59E0B] hover:bg-[#D97706] rounded-full"
                          data-testid="leave-review-btn"
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Leave a Review
                        </Button>
                      </>
                    ) : (
                      // Show the appropriate review form based on role
                      reviewStatus.my_role === 'buyer' ? (
                        <BuyerReviewForm
                          matchId={match.id}
                          revieweeUsername={match.owner_username}
                          api={api}
                          onSuccess={() => {
                            setShowReviewForm(false);
                            fetchReviewStatus();
                            toast.success('Thank you for your review!');
                          }}
                          onCancel={() => setShowReviewForm(false)}
                        />
                      ) : (
                        <OwnerReviewForm
                          matchId={match.id}
                          revieweeUsername={match.wanter_username}
                          api={api}
                          onSuccess={() => {
                            setShowReviewForm(false);
                            fetchReviewStatus();
                            toast.success('Thank you for your review!');
                          }}
                          onCancel={() => setShowReviewForm(false)}
                        />
                      )
                    )}
                  </>
                ) : reviewStatus.has_reviewed ? (
                  // Already reviewed
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#059669]" />
                    <div>
                      <p className="font-medium text-[var(--theme-text-primary)]">Review Submitted</p>
                      <p className="text-sm text-[var(--theme-text-secondary)]">
                        Thank you for your feedback!
                      </p>
                    </div>
                  </div>
                ) : (
                  // Review window expired
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[var(--theme-text-muted)]" />
                    <div>
                      <p className="font-medium text-[var(--theme-text-primary)]">Review Window Closed</p>
                      <p className="text-sm text-[var(--theme-text-secondary)]">
                        The 7-day review period has ended.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mark Complete Section - Only show if not completed */}
            {match.status !== 'completed' && (
              <div className="p-5 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)] shadow-card">
                <h3 className="font-bold text-[var(--theme-text-primary)] mb-3">Complete Deal</h3>
                <p className="text-sm text-[var(--theme-text-secondary)] mb-4">
                  Once you've agreed on terms and completed the transaction offline, mark this deal as done.
                </p>
                <Button
                  onClick={handleCompleteTransaction}
                  disabled={completing}
                  className="w-full bg-[#059669] hover:bg-[#047857] rounded-full h-12"
                  data-testid="complete-deal-btn"
                >
                  {completing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Mark Deal as Complete
                    </>
                  )}
                </Button>
              </div>
            )}
        </div>
      </div>
    </Layout>
  );
}
